import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

const PaymentHistoryTable = ({ transactions = [], loading = false, error = '', cancelAtPeriodEnd = false, cancelDate = '' }) => {
  const { t } = useTranslation();
  const emDash = t('common.emDash');
  const formatCardLast4 = (last4) => (last4 ? `•••• ${last4}` : emDash);
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'text-success bg-success/10';
      case 'pending':
        return 'text-warning bg-warning/10';
      case 'failed':
      case 'cancelled':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'CheckCircle2';
      case 'pending':
        return 'Clock';
      case 'failed':
      case 'cancelled':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const todayFormatted = new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
  const displayTransactions = [...(cancelAtPeriodEnd && cancelDate
    ? [{
      id: 'cancel-event',
      date: todayFormatted,
      description: t('billing.cancelledAccess', { date: cancelDate }),
      amount: emDash,
      cardLast4: null,
      status: 'cancelled',
      statusLabel: t('billing.cancelled'),
    }]
    : []), ...(transactions ?? [])];

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-elevation-md">
      <div className="p-4 md:p-6 border-b border-border">
        <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
          {t('billing.paymentHistory')}
        </h3>
        <p className="text-sm md:text-base text-muted-foreground mt-1">
          {t('billing.paymentHistorySubtitle')}
        </p>
      </div>
      {error && (
        <div className="p-4 md:p-6">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
      {loading && (
        <div className="p-8 md:p-12 text-center text-muted-foreground text-sm">
          {t('common.loadingEllipsis')}
        </div>
      )}
      {!loading && !error && displayTransactions.length === 0 && (
        <div className="p-8 md:p-12 text-center text-muted-foreground text-sm">
          {t('billing.noPayments')}
        </div>
      )}
      {!loading && !error && displayTransactions.length > 0 && (
        <>
          {/* Desktop View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-4 text-sm font-semibold text-foreground">{t('billing.date')}</th>
                  <th className="text-left p-4 text-sm font-semibold text-foreground">{t('billing.description')}</th>
                  <th className="text-left p-4 text-sm font-semibold text-foreground">{t('billing.amount')}</th>
                  <th className="text-left p-4 text-sm font-semibold text-foreground">{t('billing.card')}</th>
                  <th className="text-left p-4 text-sm font-semibold text-foreground">{t('billing.status')}</th>
                </tr>
              </thead>
              <tbody>
                {displayTransactions.map((transaction, index) => (
                  <tr
                    key={transaction?.id}
                    className={`${index % 2 === 0 ? 'bg-background' : 'bg-muted/20'} hover:bg-muted/40 transition-colors`}
                  >
                    <td className="p-4 text-sm text-foreground">{transaction?.date}</td>
                    <td className="p-4 text-sm text-foreground">{transaction?.description}</td>
                    <td className="p-4 text-sm font-medium text-foreground">{transaction?.amount === emDash || transaction?.amount === '—' ? emDash : `€${transaction?.amount}`}</td>
                    <td className="p-4 text-sm text-muted-foreground tabular-nums">
                      {formatCardLast4(transaction?.cardLast4)}
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction?.status)}`}>
                        <Icon name={getStatusIcon(transaction?.status)} size={14} />
                        {transaction?.statusLabel || transaction?.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile View */}
          <div className="md:hidden divide-y divide-border">
            {displayTransactions.map((transaction) => (
              <div key={transaction?.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{transaction?.description}</p>
                    <p className="text-xs text-muted-foreground mt-1">{transaction?.date}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction?.status)} flex-shrink-0 ml-2`}>
                    <Icon name={getStatusIcon(transaction?.status)} size={12} />
                    {transaction?.statusLabel || transaction?.status}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-base font-semibold text-foreground">{transaction?.amount === emDash || transaction?.amount === '—' ? emDash : `€${transaction?.amount}`}</span>
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {formatCardLast4(transaction?.cardLast4)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentHistoryTable;
